import { cn } from "@/lib/utils";
import { Glasses, Info, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDataTableSettings } from "../../data-table/hooks/useDataTableSettings";
import { PropsWithChildren } from "react";
import { useDataTableQuery } from "@/components/data-table/hooks/useDataTableQuery";
import { QueryTableLoadMoreButton } from "./QueryTableLoadMoreButton";
import { InfiniteData, UseInfiniteQueryResult } from "@tanstack/react-query";

interface QueryTableStatusProps extends PropsWithChildren {
  className?: string;
}

export const QueryTableStatus = <TData extends { id: string }>({
  className,
  children,
}: QueryTableStatusProps) => {
  const { t } = useTranslation();
  const { options, table } = useDataTableSettings<TData>();
  const { query } = useDataTableQuery<TData>();
  const defaultClassName = "flex items-center gap-4 text-sm text-slate-600";

  if (query.isError) {
    return (
      <span className={cn(defaultClassName, className)}>
        <XCircle className="w-6 h-6 shrink-0" />
        <span>{t("table.error")}</span>
      </span>
    );
  }

  const isPaginated = query.data !== undefined && "pages" in query.data;

  const infiniteQuery = isPaginated
    ? (query as UseInfiniteQueryResult<InfiniteData<TData>>)
    : null;

  return (
    <span
      className={cn(
        "flex gap-5 text-sm text-slate-600 overflow-x-auto",
        defaultClassName,
        className,
      )}
    >
      {infiniteQuery && (
        <QueryTableLoadMoreButton
          isFetching={infiniteQuery.isFetching}
          hasNextPage={infiniteQuery.hasNextPage}
          fetchNextPage={infiniteQuery.fetchNextPage}
        />
      )}
      {children}
      <span className="flex items-center gap-2 shrink-0">
        <Info className="w-4 h-4" />
        <span>{t("table.status", { count: table.getRowCount() })}</span>
      </span>
      {options.isFilteredOrSorted() && (
        <span className="flex shrink-0 items-center gap-2 text-blue-600">
          <Glasses className="w-4 h-4" />
          <span>{t("table.tableIsFilteredOrSorted")}</span>
        </span>
      )}
    </span>
  );
};
